import { useState } from "react";
import { Box, Flex, Text, Icon, Switch } from "@chakra-ui/react";
import { FaBriefcase, FaFolder, FaRegCalendarAlt, FaRegClock, FaPen, FaTrashAlt, FaPlus, FaExternalLinkAlt } from "react-icons/fa";
import { Service } from "./types";

type ServicesTableProps = {
  services: Service[];
  onDelete: (id: string) => void;
  onAddClick: () => void;
};

const ServicesTable = ({ services, onDelete, onAddClick }: ServicesTableProps) => {
  const [tab, setTab] = useState<"services" | "categories">("services");
  const [onlyActive, setOnlyActive] = useState(false);

  return (
    <Box>
      <Flex gap={1.5} mb={2.5}>
        <Flex
          as="button"
          type="button"
          onClick={() => setTab("services")}
          align="center"
          gap={1}
          fontSize="8px"
          fontWeight="700"
          borderRadius="full"
          px={2.5}
          py={1.5}
          bg={tab === "services" ? "wobble.600" : "chalk.100"}
          color={tab === "services" ? "white" : "slate.700"}
        >
          <Icon as={FaBriefcase} boxSize="8px" />
          Szolgáltatások
        </Flex>
        <Flex
          as="button"
          type="button"
          onClick={() => setTab("categories")}
          align="center"
          gap={1}
          fontSize="8px"
          fontWeight="700"
          borderRadius="full"
          px={2.5}
          py={1.5}
          bg={tab === "categories" ? "wobble.600" : "chalk.100"}
          color={tab === "categories" ? "white" : "slate.700"}
        >
          <Icon as={FaFolder} boxSize="8px" />
          Kategóriák
        </Flex>
      </Flex>

      <Flex align="center" gap={1.5} fontSize="8px" fontWeight="600" color="wobble.700" mb={3}>
        <Icon as={FaRegCalendarAlt} boxSize="8px" />
        <Text>Foglalási oldal</Text>
        <Icon as={FaExternalLinkAlt} boxSize="6px" />
      </Flex>

      {tab === "categories" ? (
        <Flex direction="column" align="center" justify="center" py={8} border="1px dashed" borderColor="chalk.400" borderRadius="lg">
          <Icon as={FaFolder} boxSize="18px" color="chalk.500" mb={2} />
          <Text fontSize="8px" color="slate.500">
            Nincs még kategória
          </Text>
        </Flex>
      ) : (
        <Box>
          <Flex justify="space-between" align="center" mb={2}>
            <Text fontWeight="800" fontSize={{ base: "sm", sm: "md" }} color="ink.900">
              Szolgáltatások ({services.length})
            </Text>
            <Flex as="button" type="button" onClick={onAddClick} align="center" gap={1} bg="wobble.600" color="white" fontSize={{ base: "7px", sm: "9px" }} fontWeight="700" borderRadius="full" px={{ base: 2, sm: 3 }} py={1.5}>
              <Icon as={FaPlus} boxSize="7px" />
              <Text display={{ base: "none", sm: "block" }}>Új szolgáltatás</Text>
            </Flex>
          </Flex>

          <Flex align="center" gap={1.5} mb={2.5}>
            <Switch size="sm" colorScheme="cyan" isChecked={onlyActive} onChange={() => setOnlyActive((v) => !v)} />
            <Text fontSize="8px" color="slate.600">
              Csak aktív szolgáltatások
            </Text>
          </Flex>

          <Flex fontSize={{ base: "7px", sm: "9px" }} fontWeight="700" color="slate.500" pb={1.5} borderBottom="1px solid" borderColor="chalk.300" mb={1.5}>
            <Box w={{ base: "26px", sm: "36px" }} flexShrink={0} />
            <Box flex="2">Szolgáltatás</Box>
            <Box flex="1" display={{ base: "none", sm: "block" }}>
              Típus
            </Box>
            <Box w="46px" display={{ base: "none", sm: "block" }} textAlign="center">
              Létszám
            </Box>
            <Box flex="1" display={{ base: "none", sm: "block" }}>
              Időtartam
            </Box>
            <Box w="50px" textAlign="right">
              Műveletek
            </Box>
          </Flex>

          <Flex direction="column">
            {services.map((s) => (
              <Flex key={s.id} align="center" py={{ base: 1.5, sm: 2 }} borderBottom="1px solid" borderColor="chalk.200">
                <Flex w={{ base: "22px", sm: "30px" }} h={{ base: "22px", sm: "30px" }} borderRadius="full" bg={s.swatch} align="center" justify="center" flexShrink={0} mr={2}>
                  <Icon as={s.icon} boxSize={{ base: "9px", sm: "11px" }} color="ink.700" />
                </Flex>
                <Box flex="2" minW={0}>
                  <Text fontSize={{ base: "8px", sm: "10px" }} fontWeight="700" noOfLines={1}>
                    {s.emoji} {s.name}
                  </Text>
                  <Text fontSize={{ base: "7px", sm: "8px" }} color="slate.500" display={{ base: "block", sm: "none" }}>
                    {s.type} · {s.maxParticipants} fő · {s.duration} perc
                  </Text>
                </Box>
                <Box flex="1" display={{ base: "none", sm: "block" }}>
                  <Text fontSize="9px" color="slate.700">
                    {s.type}
                  </Text>
                </Box>
                <Box w="46px" display={{ base: "none", sm: "block" }} textAlign="center">
                  <Text fontSize="9px" color="slate.700">
                    {s.maxParticipants}
                  </Text>
                </Box>
                <Box flex="1" display={{ base: "none", sm: "block" }}>
                  <Text fontSize="9px" color="slate.700">
                    {s.duration} perc
                  </Text>
                </Box>
                <Flex w="50px" justify="flex-end" gap={{ base: 1.5, sm: 2.5 }} flexShrink={0}>
                  <Icon as={FaRegClock} boxSize={{ base: "9px", sm: "10px" }} color="wobble.600" />
                  <Icon as={FaPen} boxSize={{ base: "9px", sm: "10px" }} color="wobble.600" />
                  <Icon as={FaTrashAlt} boxSize={{ base: "9px", sm: "10px" }} color="#D64545" cursor="pointer" onClick={() => onDelete(s.id)} />
                </Flex>
              </Flex>
            ))}
            {services.length === 0 && (
              <Text fontSize="9px" color="slate.500" py={4} textAlign="center">
                Nincs még szolgáltatás, adj hozzá egyet!
              </Text>
            )}
          </Flex>
        </Box>
      )}
    </Box>
  );
};

export default ServicesTable;
